import React, { useState, useEffect } from "react";
import api from "../../api";
import Select from "react-select";
import { FaDownload, FaSearch } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../StudentIdCardPage.css";

const StudentIdCardPage = () => {
  const [form, setForm] = useState({ class_id: null, section_id: null });
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);

  const [loadingStudents, setLoadingStudents] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  /* ---------------- TOAST ---------------- */
  const showToast = (type, msg) =>
    toast[type](msg, { position: "top-right", autoClose: 2000 });

  /* ---------------- LOAD CLASSES ---------------- */
  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const res = await api.get("/get-classes");
      if (res.data.success) setClasses(res.data.data);
    } catch {
      showToast("error", "Unable to load classes");
    }
  };

  /* ---------------- LOAD SECTIONS ---------------- */
  const fetchSections = async (classId) => {
    try {
      const res = await api.get(`/get-sections/${classId}`);
      if (res.data.success) setSections(res.data.data);
      else setSections([]);
    } catch {
      showToast("error", "Unable to load sections");
    }
  };

  /* ---------------- SEARCH STUDENTS ---------------- */
  const fetchStudents = async () => {
    if (!form.class_id || !form.section_id) {
      showToast("warning", "Select class and section");
      return;
    }

    setLoadingStudents(true);
    setStudents([]);

    try {
      const res = await api.get(
        `/id_card/get-students?class_id=${form.class_id}&section_id=${form.section_id}`
      );

      if (res.data.success) setStudents(res.data.data);
      else setStudents([]);
    } catch {
      showToast("error", "Failed to load students");
    } finally {
      setLoadingStudents(false);
    }
  };

  /* ---------------- DOWNLOAD ID CARD (REAL DOWNLOAD) ---------------- */
  const downloadCard = async (
    id,
    studentName,
    studentCode,
    type = "landscape" // default
  ) => {
    try {
      setDownloadingId(id);

      const res = await api.get(
        `/id_card/view/${id}?dl=1&type=${type}`,
        {
          responseType: "blob", // 🔑 REQUIRED
        }
      );

      const blob = new Blob([res.data], { type: "image/jpeg" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${studentName.replace(/\s+/g, "_")}_${studentCode}_${type.toUpperCase()}_IDCARD.jpg`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      showToast("success", "ID Card downloaded");
    } catch (error) {
      console.error(error);
      showToast("error", "Download failed");
    } finally {
      setDownloadingId(null);
    }
  };
  /* ---------------- VIEW ID CARD (MODAL PREVIEW) ---------------- */
  const viewCard = async (id, type = "portrait") => {
    try {
      setPreviewLoading(true);

      const res = await api.get(
        `/id_card/view/${id}?type=${type}`
      );

      if (res.data.success && res.data.image_url) {
        setPreviewUrl(res.data.image_url); // 🔑 DIRECT URL
      } else {
        showToast("error", "Invalid preview response");
      }

    } catch (error) {
      console.error(error);
      showToast("error", "Failed to preview ID Card");
    } finally {
      setPreviewLoading(false);
    }
  };



  /* ---------------- JSX ---------------- */
  return (
    <div className="id-container">
      <ToastContainer />

      <h2 className="title">Student ID Card Generator</h2>

      {/* FILTER */}
      <div className="filter-card">
        <div className="row">

          {/* CLASS */}
          <div className="col">
            <label>Select Class</label>
            <Select
              value={
                form.class_id
                  ? classes
                    .map(c => ({ value: c.id, label: c.class_name }))
                    .find(o => o.value === form.class_id)
                  : null
              }
              isClearable
              options={classes.map(c => ({
                value: c.id,
                label: c.class_name
              }))}
              onChange={(e) => {
                if (!e) {
                  setForm({ class_id: null, section_id: null });
                  setSections([]);
                  setStudents([]);
                  return;
                }

                setForm({ class_id: e.value, section_id: null });
                fetchSections(e.value);
              }}
              placeholder="Choose Class"
            />
          </div>

          {/* SECTION */}
          <div className="col">
            <label>Select Section</label>
            <Select
              value={
                form.section_id
                  ? sections
                    .map(s => ({ value: s.id, label: s.section_name }))
                    .find(o => o.value === form.section_id)
                  : null
              }
              isClearable
              isDisabled={!form.class_id}
              options={sections.map(s => ({
                value: s.id,
                label: s.section_name
              }))}
              onChange={(e) =>
                setForm({ ...form, section_id: e ? e.value : null })
              }
              placeholder="Choose Section"
            />
          </div>

          {/* SEARCH BUTTON */}
          <div className="col action">
            <button
              className="btn-primary"
              onClick={fetchStudents}
              disabled={loadingStudents}
              style={{
                opacity: loadingStudents ? 0.7 : 1,
                cursor: loadingStudents ? "not-allowed" : "pointer",
              }}
            >
              <FaSearch />
              {loadingStudents ? " Searching..." : " Search Students"}
            </button>
          </div>
        </div>
      </div>

      {/* STUDENT TABLE */}
      <div className="list-card">
        <h4 className="list-title">Student List</h4>

        {loadingStudents && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Loading students...
          </div>
        )}

        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>Photo</th>
              <th>Name</th>
              <th>Roll</th>
              <th>Class</th>
              <th>Father</th>
              <th>Aadhaar</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {!loadingStudents && students.length === 0 && (
              <tr>
                <td colSpan="8" style={{ textAlign: "center" }}>
                  No students found
                </td>
              </tr>
            )}

            {students.map((stu, i) => (
              <tr key={stu.id}>
                <td>{i + 1}</td>
                <td>
                  <img src={stu.photo} className="student-img" alt="" />
                </td>
                <td>{stu.name}</td>
                <td>{stu.roll_no}</td>
                <td>{stu.class_name}-{stu.section_name}</td>
                <td>{stu.father_name}</td>
                <td>{stu.aadhaar_no}</td>
                <td>
                  {/* VIEW BUTTON */}
                  <button
                    className="btn-view"
                    style={{ marginBottom: "6px" }}
                    onClick={() => viewCard(stu.id, "portrait")}
                  >
                    👁 View ID Card
                  </button>
                  <button
                    className="btn-download"
                    disabled={downloadingId === stu.id}
                    style={{
                      opacity: downloadingId === stu.id ? 0.6 : 1,
                      cursor: downloadingId === stu.id ? "not-allowed" : "pointer",
                    }}
                    onClick={() =>
                      downloadCard(stu.id, stu.name, stu.student_code, "portrait")
                    }
                  >
                    <FaDownload />{" "}
                    {downloadingId === stu.id ? "Downloading..." : "Download Portrait"}
                  </button>
                  <button
                    className="btn-download"
                    disabled={downloadingId === stu.id}
                    style={{
                      opacity: downloadingId === stu.id ? 0.6 : 1,
                      cursor: downloadingId === stu.id ? "not-allowed" : "pointer",
                    }}
                    onClick={() =>
                      downloadCard(stu.id, stu.name, stu.student_code, "landscape")
                    }
                  >
                    <FaDownload />{" "}
                    {downloadingId === stu.id ? "Downloading..." : "Download Landscape"}
                  </button>

                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* ================= PREVIEW MODAL ================= */}
        {previewUrl && (
          <div className="preview-modal">
            <div className="preview-box">

              <button
                className="close-btn"
                onClick={() => setPreviewUrl(null)}
              >
                ✖
              </button>

              {previewLoading ? (
                <div className="loading">Loading preview...</div>
              ) : (
                <img
                  src={previewUrl}
                  alt="ID Card Preview"
                  style={{ width: "100%", borderRadius: "6px" }}
                />
              )}

            </div>
          </div>
        )}


      </div>
    </div>

  );
};

export default StudentIdCardPage;
